import type * as React from 'react'
import { useEffect, useState } from 'react'
import type { JSX } from 'react'

import {
  Flex,
  Button,
  Form,
  Input,
  Text,
  CriticalIcon,
  useDebounceCallback,
} from '@buffer-mono/popcorn'

import {
  useAccount,
  useHasOrganizationAction,
} from '~publish/contexts/AccountContext'
import { getEntriesWithKeys } from '~publish/helpers/getEntriesWithKeys'

import style from '../../Settings.module.css'
import { useInputValidation } from '../../shared/hooks/useInputValidation'
import { organizationNameSchema } from '../../shared/validation/organizationName'
import { useUpdateOrganizationName } from '../hooks/useUpdateOrganizationName'

const OrganizationName = (): JSX.Element => {
  const { account } = useAccount()
  const canEditOrganization = useHasOrganizationAction('edit')
  const currentOrganizationName = account?.currentOrganization?.name ?? null
  const [newOrganizationName, setNewOrganizationName] = useState(
    currentOrganizationName,
  )

  const { updateOrganizationName, isLoading } = useUpdateOrganizationName()

  const [invalidInput, setInvalidInput] = useState(false)
  const [isDisabled, setIsDisabled] = useState(
    currentOrganizationName === newOrganizationName,
  )

  const { checkIsValid, isValid, errorMessages } = useInputValidation({
    schema: organizationNameSchema,
    inputValue: newOrganizationName,
  })

  useEffect(() => {
    if (currentOrganizationName === newOrganizationName) {
      setIsDisabled(true)
    } else {
      setIsDisabled(false)
    }
  }, [currentOrganizationName, newOrganizationName])

  const handleSubmit = async (): Promise<void> => {
    const result = await updateOrganizationName({
      organizationId: account?.currentOrganization?.id ?? '',
      name: newOrganizationName?.trim() ?? '',
    })

    if (result) {
      setInvalidInput(false)
    }
  }

  const handleValidateInput = useDebounceCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const inputValue = event.target.value
      const trimmedValue = inputValue.trim()
      setNewOrganizationName(trimmedValue)

      const inputIsValid = checkIsValid({ inputValue: trimmedValue })
      setInvalidInput(!inputIsValid)
    },
    200,
  )

  return (
    <Form
      name="updateOrganizationNameForm"
      className={style.fullWidth}
      onSubmit={handleSubmit}
    >
      <Flex gap="sm" direction="row" align="end">
        <Form.Field name="organizationName" className={style.fullWidth}>
          <Form.Label htmlFor="organizationName">Organization Name</Form.Label>
          <Form.Control>
            <span>
              <Input
                id="organizationName"
                defaultValue={currentOrganizationName ?? ''}
                autoComplete="organization"
                aria-invalid={invalidInput}
                onChange={handleValidateInput}
                className={style.formInput}
                disabled={
                  currentOrganizationName === null || !canEditOrganization
                }
              />
            </span>
          </Form.Control>
        </Form.Field>
        {canEditOrganization && (
          <Button
            size="medium"
            type="submit"
            variant="secondary"
            disabled={isDisabled || !isValid || isLoading}
            className={style.fixedWidthButton}
            loading={isLoading}
          >
            Save Changes
          </Button>
        )}
      </Flex>
      {invalidInput &&
        errorMessages &&
        getEntriesWithKeys(errorMessages, (error) => error).map(
          ({ key, item: error }) => (
            <Text
              key={key}
              color="critical"
              size="sm"
              icon={<CriticalIcon size="xsmall" />}
            >
              {error}
            </Text>
          ),
        )}
    </Form>
  )
}

export default OrganizationName
